import type {
  Round,
  Sg2Round,
  StartRoundPayload,
  StartUnityGameDto,
} from "Models";
import type { MoveRoundPayload, UseDataInfo } from "QueryTypes";
import { type RunMutation, useMutation, useRequest } from "redux-query-react";
import { useMemo } from "react";
import {
  getRoundByIdQuery,
  makeMoveRoundMutation,
  makeStartRoundMutation,
  makeStartUnityGameMutation,
} from "./roundQueries";
import { useSelector } from "react-redux";
import type { RootState } from "StoreTypes";
import type { EntitiesState, QueryState } from "redux-query";

export const useRoundById = (
  roundId: string | undefined,
): UseDataInfo<Round | Sg2Round | undefined> => {
  const safeQuery = useMemo(() => {
    if (!roundId?.trim()) {
      //console.log('roundId is falsy or empty');
      return undefined;
    }
    return getRoundByIdQuery(roundId);
  }, [roundId]); // Add refreshKey to dependencies

  const [queryState, refreshRound] = useRequest(safeQuery);
  const roundById = useSelector<RootState, Round | Sg2Round | undefined>(
    (state: RootState): Round | Sg2Round | undefined => {
      if (!roundId) return undefined;
      return state.entities.rounds?.[roundId];
    },
  );

  return [roundById, queryState, refreshRound];
};

export const useStartRound = (
  facilityId: string,
  simulatorId: string,
): [QueryState, RunMutation<EntitiesState, [StartRoundPayload]>] => {
  const [startRoundQueryState, startRound] = useMutation(
    (body: StartRoundPayload) =>
      makeStartRoundMutation(facilityId, simulatorId, body),
  );
  return [startRoundQueryState, startRound];
};

export const useMoveRound = (
  roundId: string,
): [QueryState, RunMutation<EntitiesState, [MoveRoundPayload]>] => {
  const [moveRoundQueryState, moveRound] = useMutation(
    (body: MoveRoundPayload) => makeMoveRoundMutation(roundId, body),
  );

  return [moveRoundQueryState, moveRound];
};

export const useStartUnityGame = (
  facilityId: string,
  simulatorId: string,
): [QueryState, RunMutation<EntitiesState, [StartUnityGameDto]>] => {
  const [startUnityGameQueryState, startUnityGame] = useMutation(
    (body: StartUnityGameDto) =>
      makeStartUnityGameMutation(facilityId, simulatorId, body),
  );
  return [startUnityGameQueryState, startUnityGame];
};
